import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';

const FilterPanel = ({ availableFilters, onFilterChange, activeFilters, quickFilters = [] }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const labelMap = { name: 'Navn', owner: 'Ansvarlig', company: 'Selskap', contact: 'Kontaktperson', title: 'Tittel', status: 'Status', industry: 'Bransje', city: 'By', country: 'Land', gdpr_status: 'GDPR Status', source: 'Kilde', tag: 'Tagg', budget: 'Budsjett', target: 'Mål', created_at: 'Opprettet', lastContact: 'Siste kontakt' };

  const handleAddFilter = (fieldKey) => {
    if (!activeFilters[fieldKey]) {
      onFilterChange({ ...activeFilters, [fieldKey]: '' });
    } else {
      const newFilters = { ...activeFilters };
      delete newFilters[fieldKey];
      onFilterChange(newFilters);
    }
    setIsDropdownOpen(false);
    setIsExpanded(true);
  };

  const handleRemoveFilter = (fieldKey) => {
    const newFilters = { ...activeFilters };
    delete newFilters[fieldKey];
    onFilterChange(newFilters);
  };

  const handleClearAll = () => {
    onFilterChange({});
  };

  const handleValueChange = (fieldKey, value) => {
    onFilterChange({ ...activeFilters, [fieldKey]: value });
  };

  const hasActiveFilters = Object.keys(activeFilters).length > 0;

  return (
    <div className="space-y-4 mb-8">
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="flex-1 flex gap-2">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <SafeIcon icon="🔍" className="h-5 w-5 text-slate-400" />
            </div>
            <input type="text" placeholder="Hurtigsøk..." className="w-full pl-11 pr-4 py-3 bg-white border border-slate-200 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 outline-none shadow-sm transition-all font-medium" value={activeFilters.name || ''} onChange={(e) => handleValueChange('name', e.target.value)} />
          </div>
          <button onClick={() => setIsExpanded(!isExpanded)} className={`px-5 py-3 rounded-2xl border flex items-center gap-2 font-black text-xs uppercase tracking-[0.15em] transition-all ${isExpanded || hasActiveFilters ? 'bg-primary-50 border-primary-200 text-primary-700' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
            <SafeIcon icon="⚡" className="w-4 h-4" /> Filter {hasActiveFilters && (
              <span className="flex items-center justify-center w-5 h-5 bg-primary-600 text-white rounded-full text-[10px] ml-1"> {Object.keys(activeFilters).length} </span>
            )}
            <SafeIcon icon="▼" className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
          </button>
        </div>
        {quickFilters.length > 0 && (
          <div className="flex items-center gap-2 overflow-x-auto pb-1 lg:pb-0 scrollbar-hide">
            {quickFilters.map((qf, idx) => (
              <button key={idx} onClick={() => qf.onClick()} className={`whitespace-nowrap px-4 py-3 rounded-2xl text-xs font-black uppercase tracking-widest border transition-all ${qf.isActive ? 'bg-slate-900 border-slate-900 text-white shadow-lg' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-400'}`}>
                {qf.label}
              </button>
            ))}
          </div>
        )}
      </div>
      <AnimatePresence>
        {isExpanded && (
          <motion.div initial={{ height: 0, opacity: 1 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 1 }} className="overflow-visible">
            <div className="bg-white border border-slate-200 rounded-[2rem] p-5 shadow-xl shadow-slate-200/50">
              <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-100">
                <div>
                  <h3 className="text-xl font-black text-slate-900 tracking-tight">Avansert Filtrering</h3>
                  <p className="text-sm text-slate-500 font-medium">Tilpass visningen ved å legge til spesifikke regler</p>
                </div>
                <div className="flex items-center gap-4">
                  {hasActiveFilters && (
                    <button onClick={handleClearAll} className="flex items-center gap-2 text-xs font-black text-slate-400 hover:text-red-500 uppercase tracking-widest transition-colors">
                      <SafeIcon icon="🔄" className="w-3.5 h-3.5" /> Nullstill alle
                    </button>
                  )}
                  <div className="relative">
                    <button onClick={() => setIsDropdownOpen(!isDropdownOpen)} className="px-6 py-3 bg-slate-900 text-white rounded-xl text-xs font-black uppercase tracking-widest flex items-center gap-2 hover:bg-slate-800 transition-all shadow-lg shadow-slate-200">
                      <SafeIcon icon="➕" className="w-4 h-4" /> Legg til regel
                    </button>
                    <AnimatePresence>
                      {isDropdownOpen && (
                        <motion.div initial={{ opacity: 0, scale: 0.95, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 10 }} className="absolute right-0 mt-2 w-72 bg-white rounded-2xl shadow-2xl border border-slate-100 z-[100] py-2">
                          <div className="px-4 py-2 mb-1">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Tilgjengelige felt</p>
                          </div>
                          {availableFilters.filter(filter => ['name', 'industry', 'city', 'status', 'owner'].includes(filter.key)).map((filter) => {
                            const isActive = Object.prototype.hasOwnProperty.call(activeFilters, filter.key);
                            return (
                              <button key={filter.key} onClick={() => handleAddFilter(filter.key)} role="switch" aria-checked={isActive} className="w-full text-left px-5 py-3 text-sm font-bold flex items-center justify-between transition-colors text-slate-700 hover:bg-primary-50 hover:text-primary-700">
                                {labelMap[filter.key] || filter.label}
                                <span className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isActive ? 'bg-primary-600' : 'bg-slate-200'}`}>
                                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${isActive ? 'translate-x-6' : 'translate-x-1'}`} />
                                </span>
                              </button>
                            );
                          })}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </div>
              {!hasActiveFilters ? (
                <div className="py-3 text-center">
                  <p className="text-slate-400 font-bold text-sm uppercase tracking-widest">Ingen aktive filtre</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {Object.entries(activeFilters).map(([key, value]) => {
                    const config = availableFilters.find(f => f.key === key);
                    if (!config) return null;
                    return (
                      <motion.div layout initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} key={key} className="bg-slate-50/50 border border-slate-200 rounded-2xl p-3 group hover:border-primary-200 hover:bg-white transition-all">
                        <div className="flex justify-between items-center mb-2">
                          <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">
                            {labelMap[key] || config.label}
                          </label>
                          <button onClick={() => handleRemoveFilter(key)} className="p-1 text-slate-300 hover:text-red-500 transition-colors">
                            <SafeIcon icon="✕" className="w-4 h-4" />
                          </button>
                        </div>
                        {config.type === 'select' ? (
                          <div className="relative">
                            <select value={value} onChange={(e) => handleValueChange(key, e.target.value)} className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-sm font-bold text-slate-900 outline-none focus:border-primary-500 focus:ring-4 focus:ring-primary-500/5 transition-all appearance-none cursor-pointer">
                              <option value="">Alle</option>
                              {config.options.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-slate-400">
                              <SafeIcon icon="▼" className="w-3 h-3" />
                            </div>
                          </div>
                        ) : (
                          <input type={config.type || 'text'} value={value} onChange={(e) => handleValueChange(key, e.target.value)} className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-sm font-bold text-slate-900 outline-none focus:border-primary-500 focus:ring-4 focus:ring-primary-500/5 transition-all" placeholder={config.placeholder || 'Skriv her...'} />
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FilterPanel;